import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { logout, isTokenExpired, isAuthenticated } from '../services/authService';

interface UseSessionTimeoutOptions {
  /**
   * Timeout in milliseconds after session expires to automatically logout
   * Default: 5 minutes (300000ms)
   */
  timeoutDelay?: number;
  /**
   * Check interval in milliseconds to verify token status
   * Default: 30 seconds (30000ms)
   */
  checkInterval?: number;
  /**
   * Callback to execute before automatic logout
   */
  onBeforeLogout?: () => void;
  /**
   * Callback to execute when session expires (but before timeout)
   */
  onSessionExpired?: () => void;
}

export const useSessionTimeout = (options: UseSessionTimeoutOptions = {}) => {
  const {
    timeoutDelay = 300000, // 5 minutes
    checkInterval = 30000,  // 30 seconds
    onBeforeLogout,
    onSessionExpired
  } = options;

  const navigate = useNavigate();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const sessionExpiredNotifiedRef = useRef(false);

  const performLogout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    onBeforeLogout?.();
    logout();
    navigate('/login', { 
      replace: true,
      state: { message: 'Session expired. Please log in again.' }
    });
  }, [navigate, onBeforeLogout]);

  const startLogoutTimer = useCallback(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Notify about session expiry (only once)
    if (!sessionExpiredNotifiedRef.current) {
      sessionExpiredNotifiedRef.current = true;
      onSessionExpired?.();
    }

    // Set timeout for automatic logout
    timeoutRef.current = setTimeout(() => {
      performLogout();
    }, timeoutDelay);
  }, [timeoutDelay, performLogout, onSessionExpired]);

  const checkTokenStatus = useCallback(() => {
    // If user is not authenticated at all, don't check
    if (!isAuthenticated()) {
      return;
    }

    // If token is expired, start the logout timer
    if (isTokenExpired()) {
      startLogoutTimer();
    } else {
      // Reset the session expired notification flag if token is valid
      sessionExpiredNotifiedRef.current = false;
      
      // Clear any existing timeout since token is still valid
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [startLogoutTimer]);

  const resetTimeout = useCallback(() => {
    sessionExpiredNotifiedRef.current = false;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    // Initial check
    checkTokenStatus();

    // Set up interval to periodically check token status
    intervalRef.current = setInterval(checkTokenStatus, checkInterval);

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [checkTokenStatus, checkInterval]);

  return {
    resetTimeout,
    checkTokenStatus
  };
};